import { defineStore } from 'pinia';
import moment from 'moment';

export const useMarketDataStore = defineStore('marketData', {
  state: () => ({
    status: null,
    schema: null,
    model: null,
    results: null,
    modelSetUp: false,
    exchanges: [], // preloaded exchange list available to UI
  }),
  getters: {
    configInput: (state) => (state.schema ? state.schema.input : {}),
    configSubmit: (state) => (state.schema ? state.schema.submit : {}),
    configDownload: (state) => (state.schema ? state.schema.download : {}),
    configResults: (state) => (state.schema ? state.schema.results : {}),
    body: (state) => (set) => {
      let results = { ...state.results };
      let path;
      if (set.match(/\]/gm)) {
        path = set.split(/\[|\]/).filter(i => i.length);
      } else {
        path = set.split('.');
      }
      path.forEach((prop) => {
        results = results[prop];
      });
      return results;
    },
    formIsValid: (state) => (obj = {}) => {
    const skipName = obj.name;
    const isValidValue = (v) => {
      if (Array.isArray(v)) return v.length > 0;
      if (v === null || v === undefined) return false;
      if (typeof v === 'string') return v.trim().length > 0;
      return true;
    };

    if (!state.schema?.input?.groups) {
      // fallback: validate present model entries
      const keys = Object.keys(state.model || {});
      if (keys.length === 0) return false;
      return keys.every(k => (k === skipName) ? true : isValidValue(state.model[k]));
    }

    const visibleNames = [];

    for (const g of state.schema.input.groups || []) {
      if (!g.controls) continue;
      
      // evaluate group-level visibility first
      let groupVisible = true;
      const groupCond = g.condition;
      if (groupCond !== undefined && groupCond !== null) {
        const field = groupCond.field;
        const value = groupCond.value;
        if (Array.isArray(value)) {
          groupVisible = Array.isArray(field)
            ? field.every(f => value.includes(state.model?.[f]))
            : value.includes(state.model?.[field]);
        } else {
          groupVisible = state.model?.[field] === value;
        }
      }
      
      // skip the entire group if it's not visible
      if (!groupVisible) continue;
      
      for (const c of g.controls) {
        if (!c?.name) continue;
        // skip UI-only controls
        if (['MDDownload','MDSubmit','MDLink'].includes(c.type)) continue;

        // evaluate top-level control visibility
        let visible = true;
        
        const cond = c.condition;
        if (cond !== undefined && cond !== null) {
          const field = cond.field;
          const value = cond.value;
          if (Array.isArray(value)) {
            visible = Array.isArray(field)
              ? field.every(f => value.includes(state.model?.[f]))
              : value.includes(state.model?.[field]);
          } else {
            visible = state.model?.[field] === value;
          }
        } else if (c.visible === false) {
          visible = false;
        }
        
        // Only add to validation if visible and not optional
        if (visible && !c.optional) {
          visibleNames.push(c.name);
        }

        // include secondary control if present; its condition governs visibility
        const sec = c.data?.secondary;
        if (sec && sec.name) {
          let secVisible = true;
          const scond = sec.condition;
          if (scond !== undefined && scond !== null) {
            const sfield = scond.field;
            const svalue = scond.value;
            if (Array.isArray(svalue)) {
              secVisible = Array.isArray(sfield)
                ? sfield.every(f => svalue.includes(state.model?.[f]))
                : svalue.includes(state.model?.[sfield]);
            } else {
              secVisible = state.model?.[sfield] === svalue;
            }
          }
          
          // Only add secondary control to validation if visible and not optional
          if (secVisible && !sec.optional) {
            visibleNames.push(sec.name);
          }
        }
      }
    }

    if (visibleNames.length === 0) {
      // nothing to validate (avoid empty-every true): treat as invalid
      return false;
    }

    // validate only visible control names
    return visibleNames.every(name => {
      if (name === skipName) return true;
      return isValidValue(state.model ? state.model[name] : undefined);
    });
  },
  },
  actions: {
    setExchanges(list = []) {
      // normalize to array
      this.exchanges = Array.isArray(list) ? list : [];
    },
    loadSchema(payload) {
      this.schema = payload;
    },
    createModel() {
      const reduceControls = (model, { controls }) => {
        controls.forEach((c) => {
          if (c.type !== 'MDDownload' && c.type !== 'MDSubmit' && c.type !== 'MDLink') {
            if (!model[c.name]) {
              model[c.name] = null;
            }
          }
        });
        return model;
      };
      const model = this.schema.input.groups.reduce(reduceControls, {});
      this.model = model;
    },
    setStatus({ section, success }) {
      const status = { ...this.status } || {};
      status[section] = success;
      this.status = status;
    },
    updateModel({ key, value }) {
      this.model[key] = value;
      if (this.status && this.status.results && !this.schema.results?.disableResetOnInput) {
        this.status.results = null;
      }
    },
    deleteModelProperty(key) {
      delete this.model[key];
    },
    loadResults(payload) {
      this.results = payload;
    },
    updateModelSetUp(payload) {
      this.modelSetUp = payload;
    },
    async get(endpoint) {
      let success = false;
      let data;
      try {
        const rsp = await fetch(endpoint);
        const text = await rsp.text();
        data = text.length ? JSON.parse(text) : {};
        if (data) success = true;
      } catch (error) {
        console.error('[marketData] GET error:', error);
      }
      return { success, data };
    },
    async submit(payload) {
      if (payload.isValid) {
        let config = [...this.schema.submit.endpoints];
        let endpointConfig;
        if (config.length > 1) {
          endpointConfig = config.find((ep) => {
            if (Array.isArray(ep.condition.value)) {
              if (Array.isArray(ep.condition.field)) {
                return ep.condition.field.every((field) => {
                  return ep.condition.value.includes(this.model[field]);
                });
              }
              return ep.condition.value.includes(this.model[ep.condition.field]);
            } else {
              return ep.condition.value === this.model[ep.condition.field];
            }
          });
        } else {
          endpointConfig = config[0];
        }

        const parts = [];
        endpointConfig.query.forEach((param) => {
          const [name, cfg] = param;
          if (cfg.condition) {
            const condFieldVal = this.model[cfg.condition.field];
            if (Array.isArray(cfg.condition.value)) {
              if (!cfg.condition.value.includes(condFieldVal)) {
                return;
              }
            } else if (condFieldVal !== cfg.condition.value) {
              return;
            }
          }
          let value;
          if (cfg.dynamic) {
            const rawVal = this.model?.[cfg.value];
            value = rawVal ?? '';
            if (cfg.format) {
              if (cfg.format.as === 'date') {
                if (new Date(value) == 'Invalid Date') {
                  value = '';
                } else {
                  value = moment(new Date(value)).format(cfg.format.schema);
                }
              } else if (cfg.format.as === 'string_upper') {
                value = (value || '').toString().toUpperCase();
              }
            }
          } else {
            value = cfg.value;
          }
          if (cfg.map && value in cfg.map) {
            value = cfg.map[value];
          }
          parts.push(`${name}=${value}`);
        });
        const query = parts.length ? `?${parts.join('&')}` : '';
        let submitAvoidsDomain = endpointConfig.avoidDomain || false;
        let endpoint;
        if (submitAvoidsDomain) {
          endpoint =
            payload.devEndpoint === 'dummy'
              ? `${endpointConfig.endpoint.dev}${query}`
              : `${endpointConfig.endpoint.prod}${query}`;
        } else {
          endpoint =
            payload.devEndpoint === 'dummy'
              ? `${endpointConfig.endpoint.dev}${query}`
              : `${payload.devEndpoint + endpointConfig.endpoint.prod}${query}`;
        }
        const section = 'results';
        const { success, data } = await this.get(endpoint);
        if (success && Object.entries(data).length > 0) {
          this.loadResults(data);
        }
        if (Object.entries(data).length === 0 || (data.status >= 400 && data.status < 500)) {
          this.setStatus({ section: 'resultsIterable', success: false });
        } else if (data?.entity && data?.entity?.pageList !== undefined && !(data.entity.pageList?.length)) {
          this.setStatus({ section: 'resultsIterable', success: false });
        } else {
          this.setStatus({ section: 'resultsIterable', success: undefined });
        }
        this.setStatus({ section, success });
      }
    },
  },
});
